import { useFetchConnections } from '@modules/salesforce/metadata-connections/hooks';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

interface GlobalStateContextProps {
  // reload primary menu
  reloadPrimaryMenu: number;
  handleReloadPrimaryMenu(): void;

  // metadata refresh
  refreshingMetadataConnectionId: Set<number>;
  handleStartRefreshingMetadata: (salesforceConnectionId?: number) => void;
  handleStopRefreshingMetadata: (salesforceConnectionId?: number) => void;

  // discard modal for create sf nlp api
  isSalesforceAPIDiscardPopUpVisible: boolean;
  handleShowSalesforceAPIDiscardPopUp(): void;
  handleHideSalesforceAPIDiscardPopUp(): void;

  // notification banner
  notificationCount: number;
  updateNotificationCount: (value: number) => void;

  // for custom object syncing
  isCustomObjectSynced: boolean;
  handleCustomObjectSyncing: (status: boolean) => void;

  children?: any;
}

const GlobalStateContext = createContext<GlobalStateContextProps | undefined>(
  undefined,
);

const GlobalStateProvider = ({ children }) => {
  const [reloadPrimaryMenu, setReloadPrimaryMenu] = useState<number>();
  // Changed from number | null to Set<number>
  const [refreshingMetadataConnectionId, setRefreshingMetadataConnectionId] =
    useState<Set<number>>(new Set());

  const [isCustomObjectSynced, setIsCustomObjectSynced] =
    useState<boolean>(false);

  const [
    isSalesforceAPIDiscardPopUpVisible,
    setIsSalesforceAPIDiscardPopUpVisible,
  ] = useState<boolean>(false);

  const [notificationCount, setNotificationCount] = useState<number>(0);

  const [
    applicationSpecificConnectionIds,
    setApplicationSpecificConnectionIds,
  ] = useState<number[]>([]);

  const query = ',connectionType@METADATA';

  const { connectionsList, isLoading, hasMore, handleLoadMore, totalPages } =
    useFetchConnections({
      queryString: query,
      defaultSize: 1000,
    });

  //update notification count
  const updateNotificationCount = useCallback((count: number) => {
    setNotificationCount(count);
  }, []);

  // reload primary menu
  const handleReloadPrimaryMenu = useCallback(() => {
    setReloadPrimaryMenu(Date.now());
  }, []);

  // toggle refresh metadata
  const handleStartRefreshingMetadata = useCallback(
    (salesforceConnectionId?: number) => {
      if (salesforceConnectionId) {
        setRefreshingMetadataConnectionId(
          (prev) => new Set([...prev, salesforceConnectionId]),
        );
      }
    },
    [],
  );

  const handleCustomObjectSyncing = useCallback((status: boolean) => {
    setIsCustomObjectSynced(status);
  }, []);

  const handleStopRefreshingMetadata = useCallback(
    (salesforceConnectionId?: number) => {
      if (salesforceConnectionId) {
        setRefreshingMetadataConnectionId((prev) => {
          const newSet = new Set(prev);
          newSet.delete(salesforceConnectionId);
          return newSet;
        });
      } else {
        setRefreshingMetadataConnectionId(new Set());
      }
    },
    [],
  );

  // toggle showDiscard popup
  const handleShowSalesforceAPIDiscardPopUp = useCallback(() => {
    setIsSalesforceAPIDiscardPopUpVisible(true);
  }, []);

  const handleHideSalesforceAPIDiscardPopUp = useCallback(() => {
    setIsSalesforceAPIDiscardPopUpVisible(false);
  }, []);

  const contextValue: GlobalStateContextProps = {
    // reload primary menu
    reloadPrimaryMenu,
    handleReloadPrimaryMenu,

    // toggle refresh metadata
    refreshingMetadataConnectionId,
    handleStartRefreshingMetadata,
    handleStopRefreshingMetadata,

    // discard modal for create sf nlp api
    isSalesforceAPIDiscardPopUpVisible,
    handleShowSalesforceAPIDiscardPopUp,
    handleHideSalesforceAPIDiscardPopUp,

    // for notificatio banner
    notificationCount,
    updateNotificationCount,

    // for custom object syncing
    isCustomObjectSynced,
    handleCustomObjectSyncing,
  };

  useEffect(() => {
    setApplicationSpecificConnectionIds(
      Array.isArray(connectionsList)
        ? connectionsList.map((item) => item.id)
        : [],
    );
  }, [connectionsList]);

  return (
    <GlobalStateContext.Provider value={contextValue}>
      {children}
    </GlobalStateContext.Provider>
  );
};

const useGlobalState = () => {
  const context = useContext(GlobalStateContext);

  if (!context) {
    throw new Error(
      'useGlobalStateKeeper must be used within a StateKeeperProvider',
    );
  }

  return context;
};

export { GlobalStateProvider, useGlobalState };
